import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import KeyboardBackspaceOutlinedIcon from "@mui/icons-material/KeyboardBackspaceOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import axios from "axios";
import { useTranslation } from "react-i18next";

import PublicRoomAvailability from "./PublicRoomAvailability";
import PublicBook from "./PublicBook";
import GeneralModal from "../../../main/components/modal/general/GeneralModal";
import { ApiConstants } from "../../../main/constants/ApiConstants";
import IHotel from "../../../main/interfaces/IHotel";
import "../../../main/theme/theme.css";
import { AppTheme, getStoredMainLandingTheme, MAIN_LANDING_THEMES } from "../../../main/theme/publicTheme";
import PublicCurrencySwitch from "./PublicCurrencySwitch";
import PublicLanguageSwitch from "./PublicLanguageSwitch";
import "./OnlineReservation.css";
import "./OnlineReservationHotels.css";
import "./PublicExperience.css";

const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

interface IPublicBookingRequestItem {
  id?: number;
  guestName: string;
  guestEmail: string;
  roomTypeName: string;
  roomNumber?: string;
  checkInDate: string;
  checkOutDate: string;
  created: string;
  status: number;
}

type IPublicBookingRequestSuccessPayload = {
  id?: number;
  guestName: string;
  guestEmail: string;
  roomTypeName: string;
  roomNumber?: string;
  checkInDate: string;
  checkOutDate: string;
  created?: string;
  status?: number;
};

const THEME_LABELS: Record<AppTheme, string> = {
  light: "Light",
  sky: "Sky",
  dark: "Dark",
  ocean: "Ocean",
  pearl: "Pearl",
  forest: "Forest",
};

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-");

const getSlugCandidates = (value: string) => {
  const normalized = toSlug(value);
  const candidates = new Set<string>();
  const trailingNumberMatch = normalized.match(/^([a-z]+)(\d+)$/);
  if (trailingNumberMatch) {
    candidates.add(`${trailingNumberMatch[1]}-${trailingNumberMatch[2]}`);
  }
  candidates.add(normalized);
  return Array.from(candidates);
};

const normalizeHotel = (hotel: any): IHotel | null => {
  if (!hotel) return null;

  return {
    ...hotel,
    id: Number(hotel?.id ?? hotel?.Id ?? 0),
    companyId: hotel?.companyId ?? hotel?.CompanyId,
    name: hotel?.name ?? hotel?.Name ?? "",
    code: hotel?.code ?? hotel?.Code ?? "",
    address: hotel?.address ?? hotel?.Address,
    timezone: hotel?.timezone ?? hotel?.Timezone,
    checkInTime: hotel?.checkInTime ?? hotel?.CheckInTime,
    checkOutTime: hotel?.checkOutTime ?? hotel?.CheckOutTime,
    currencyId: hotel?.currencyId ?? hotel?.CurrencyId,
    created: hotel?.created ?? hotel?.Created,
    deleted: hotel?.deleted ?? hotel?.Deleted,
  };
};

const RoomAvailabilityWrapper = () => {
  const { hotelName } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [theme, setTheme] = useState<AppTheme>(getStoredMainLandingTheme);
  const [selectedCurrency, setSelectedCurrency] = useState("ALL");
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const themeDropdownRef = useRef<HTMLDivElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  const [hotelId, setHotelId] = useState<number | null>(null);
  const [selectedHotel, setSelectedHotel] = useState<IHotel | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<any>(null);
  const [latestRequest, setLatestRequest] = useState<IPublicBookingRequestItem | null>(null);
  const [isCancellingRequest, setIsCancellingRequest] = useState(false);
  const slug = useMemo(() => toSlug(hotelName ?? ""), [hotelName]);
  const slugCandidates = useMemo(() => getSlugCandidates(hotelName ?? ""), [hotelName]);

  useEffect(() => {
    if (!isThemeOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!themeDropdownRef.current?.contains(event.target as Node)) {
        setIsThemeOpen(false);
      }
      if (!mobileMenuRef.current?.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsThemeOpen(false);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isThemeOpen]);

  const getStatusLabel = (status: number) => {
    switch (status) {
      case 2:
        return t("bookingMonitor.requestApproved");
      case 3:
        return t("bookingMonitor.requestRejected");
      case 4:
        return t("bookingMonitor.requestCancelled");
      case 5:
        return t("bookingMonitor.requestExpired");
      default:
        return t("bookingMonitor.requestPending");
    }
  };

  useEffect(() => {
    const loadHotel = async () => {
      if (!slug) {
        setSelectedHotel(null);
        setHotelId(null);
        return;
      }

      try {
        let matchedHotel: IHotel | null = null;

        for (const candidate of slugCandidates) {
          const response = await publicApi.get(ApiConstants.publicHotel.bySlug, {
            params: { slug: candidate },
          });

          matchedHotel = normalizeHotel(response.data);
          if (matchedHotel?.id) break;
        }

        setSelectedHotel(matchedHotel);
        setHotelId(matchedHotel?.id ?? null);

        if (!matchedHotel) {
          toast.error(t("publicBooking.hotelNotFound"));
        }
      } catch (error: any) {
        setSelectedHotel(null);
        setHotelId(null);
        toast.error(error?.response?.data || t("publicBooking.loadHotelFailed"));
      }
    };

    void loadHotel();
  }, [hotelName, slug, slugCandidates, t]);

  const handleBookingSuccess = (request: IPublicBookingRequestSuccessPayload) => {
    const nextItem: IPublicBookingRequestItem = {
      id: request.id,
      guestName: request.guestName,
      guestEmail: request.guestEmail,
      roomTypeName: request.roomTypeName,
      roomNumber: request.roomNumber,
      checkInDate: request.checkInDate,
      checkOutDate: request.checkOutDate,
      created: request.created ?? new Date().toISOString(),
      status: request.status ?? 1,
    };

    setLatestRequest(nextItem);
    navigate("/home");
  };

  const cancelPendingRequest = async () => {
    if (!latestRequest?.id) {
      toast.error(t("publicBooking.requestCancelUnavailable", { defaultValue: "This request cannot be cancelled right now." }));
      return;
    }

    if (latestRequest.status !== 1) {
      return;
    }

    try {
      setIsCancellingRequest(true);
      await publicApi.put(ApiConstants.publicUser.cancelBookingRequest, {
        id: latestRequest.id,
        guestEmail: latestRequest.guestEmail,
      });

      setLatestRequest((current) =>
        current
          ? {
              ...current,
              status: 4,
            }
          : current,
      );
      toast.success(t("publicBooking.requestCancelled", { defaultValue: "Booking request cancelled." }));
    } catch (error: any) {
      toast.error(
        error?.response?.data ||
          t("publicBooking.requestCancelFailed", { defaultValue: "Failed to cancel booking request." }),
      );
    } finally {
      setIsCancellingRequest(false);
    }
  };

  return (
    <div className={`or-hotels-page bcShell bcTheme-${theme}`}>
      <header className="or-hotels-top-nav">
        <div className="or-hotels-nav-container">
          <div className="or-hotels-nav-left">
            <button type="button" className="or-hotels-monitor-btn" onClick={() => navigate("/booking")}>
              <KeyboardBackspaceOutlinedIcon fontSize="inherit" />
              {t("generic.back")}
            </button>
            <button type="button" className="or-hotels-brand" onClick={() => navigate("/booking")}>
              <span className="or-hotels-brand-icon">
                <ApartmentOutlinedIcon fontSize="inherit" />
              </span>
              <span className="or-hotels-brand-text">{selectedHotel?.name ?? hotelName ?? t("publicBooking.hotelReservations")}</span>
            </button>
          </div>

          <div className={`or-hotels-nav-right ${isMobileMenuOpen ? "is-open" : ""}`} ref={mobileMenuRef}>
            <button
              type="button"
              className="or-hotels-mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen((current) => !current)}
              aria-haspopup="menu"
              aria-expanded={isMobileMenuOpen}
              aria-label={t("generic.menu", { defaultValue: "Menu" })}
            >
              <MenuRoundedIcon fontSize="inherit" />
            </button>

            <div className={`or-hotels-nav-actions ${isMobileMenuOpen ? "is-open" : ""}`} role="menu">
              <button
                type="button"
                className="or-hotels-monitor-btn"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate("/home");
                }}
              >
                <LoginOutlinedIcon fontSize="inherit" />
                {t("publicBooking.openMonitor")}
              </button>

              <div className="theme-dropdown or-hotels-theme-dropdown" ref={themeDropdownRef}>
                <button
                  type="button"
                  className="theme-dropdown-trigger or-hotels-theme-trigger"
                  onClick={() => setIsThemeOpen((current) => !current)}
                  aria-haspopup="menu"
                  aria-expanded={isThemeOpen}
                  aria-label={t("generic.theme", { defaultValue: "Theme" })}
                >
                  <PaletteOutlinedIcon fontSize="inherit" />
                  <span>{THEME_LABELS[theme]}</span>
                  <KeyboardArrowDownRoundedIcon fontSize="inherit" />
                </button>

                {isThemeOpen ? (
                  <div className="theme-dropdown-menu or-hotels-theme-menu" role="menu">
                    {MAIN_LANDING_THEMES.map((themeOption) => (
                      <button
                        key={themeOption}
                        type="button"
                        className={`theme-dropdown-item ${theme === themeOption ? "active" : ""}`}
                        onClick={() => {
                          setTheme(themeOption);
                          setIsThemeOpen(false);
                          setIsMobileMenuOpen(false);
                        }}
                        role="menuitemradio"
                        aria-checked={theme === themeOption}
                      >
                        <span className={`theme-dot theme-dot--${themeOption}`} />
                        <span>{THEME_LABELS[themeOption]}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <PublicCurrencySwitch value={selectedCurrency} onChange={setSelectedCurrency} />

              <PublicLanguageSwitch />
            </div>
          </div>
        </div>
      </header>

      {latestRequest ? (
        <div className="or-hotels-shell or-success-card">
          <div className="or-success-copy">
            <strong>{t("publicBooking.requestSent")}</strong>
            <span>
              {t("publicBooking.requestStatus")} <b>{getStatusLabel(latestRequest.status)}</b>
            </span>
          </div>
          <div className="or-success-actions">
            <div className={`or-status-badge status-${latestRequest.status}`}>
              <CheckCircleOutlineOutlinedIcon fontSize="inherit" />
              {getStatusLabel(latestRequest.status)}
            </div>
            {latestRequest.status === 1 ? (
              <button
                type="button"
                className="or-pending-cancel-btn"
                onClick={cancelPendingRequest}
                disabled={isCancellingRequest}
              >
                {isCancellingRequest
                  ? t("publicBooking.cancellingRequest", { defaultValue: "Cancelling..." })
                  : t("generic.cancel")}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <PublicRoomAvailability
        hotelId={hotelId}
        hotelName={selectedHotel?.name ?? hotelName ?? t("publicBooking.hotelReservations")}
        hotelCheckInTime={selectedHotel?.checkInTime || "14:00"}
        hotelCheckOutTime={selectedHotel?.checkOutTime || "11:00"}
        refreshKey={0}
        selectedCurrency={selectedCurrency}
        onDatesChange={(_checkInDate, _checkOutDate) => {}}
        onRoomSelect={(room, checkIn, checkOut, roomPhotos) => {
          setSelectedRoom({
            room,
            checkIn,
            checkOut,
            roomPhotos,
          });
        }}
      />

      {selectedRoom ? (
        <GeneralModal
          handleClose={() => setSelectedRoom(null)}
          headerName={t("publicBooking.completeRequest")}
          width="780px"
          radius="18px"
          hideFooter
        >
          <PublicBook
            hotelId={hotelId}
            room={selectedRoom.room}
            checkInDate={selectedRoom.checkIn}
            checkOutDate={selectedRoom.checkOut}
            roomPhotos={selectedRoom.roomPhotos || []}
            selectedCurrency={selectedCurrency}
            onSuccess={handleBookingSuccess}
            onClose={() => setSelectedRoom(null)}
          />
        </GeneralModal>
      ) : null}
    </div>
  );
};

export default RoomAvailabilityWrapper;
